import React, { useEffect, useState } from 'react';
import './UnitToggle.css';
import { useUnits } from '../contexts/UnitsContext';
import { getVisibleUnitKinds } from './unitVisibility';

const UNIT_SELECTOR = '[data-unit-kind="weight"],[data-unit-kind="distance"],[data-unit-kind="elevation"]';

const UnitToggle = () => {
  const { measurementSystem, toggleMeasurementSystem } = useUnits();
  const [isScrolling, setIsScrolling] = useState(false);
  const [visibleKinds, setVisibleKinds] = useState(() => new Set());

  useEffect(() => {
    const elementVisibility = new Map();
    let observer = null;
    let mutationObserver = null;
    let fallbackScan = null;
    let scrollTargets = [];
    let refreshScrollTargets = () => {};
    let scrollTimeout = null;

    const getActiveModal = () => {
      const modals = document.querySelectorAll('.modal');
      return modals.length ? modals[modals.length - 1] : null;
    };

    const isActiveSurface = (element) => {
      const activeModal = getActiveModal();
      return !activeModal || activeModal.contains(element);
    };

    const updateVisibleKinds = () => {
      const entries = Array.from(elementVisibility.entries())
        .filter(([element, isVisible]) => isVisible && document.contains(element) && isActiveSurface(element))
        .map(([element]) => ({ target: element, isIntersecting: true }));
      setVisibleKinds(getVisibleUnitKinds(entries));
    };

    const getCandidateElements = () => Array.from(document.querySelectorAll(UNIT_SELECTOR))
      .filter(isActiveSurface);

    const syncObservedElements = () => {
      const candidates = new Set(getCandidateElements());
      elementVisibility.forEach((_isVisible, element) => {
        if (!candidates.has(element)) {
          elementVisibility.delete(element);
          observer?.unobserve(element);
        }
      });
      candidates.forEach(element => {
        if (!elementVisibility.has(element)) {
          elementVisibility.set(element, false);
          observer?.observe(element);
        }
      });
      refreshScrollTargets();
      if (fallbackScan) fallbackScan();
      else updateVisibleKinds();
    };

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          elementVisibility.set(entry.target, entry.isIntersecting && entry.intersectionRatio > 0);
        });
        updateVisibleKinds();
      }, { threshold: [0, 0.01] });
      syncObservedElements();
    } else {
      const scanViewport = () => {
        const entries = getCandidateElements().map(element => {
          const rect = element.getBoundingClientRect();
          return {
            target: element,
            isIntersecting: rect.bottom > 0 && rect.top < window.innerHeight,
            intersectionRatio: 1,
          };
        });
        setVisibleKinds(getVisibleUnitKinds(entries));
      };
      fallbackScan = scanViewport;
      window.addEventListener('scroll', scanViewport, { passive: true });
      window.addEventListener('resize', scanViewport);
      scanViewport();
    }

    const onScroll = () => {
      setIsScrolling(true);
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => setIsScrolling(false), 800);
    };

    refreshScrollTargets = () => {
      scrollTargets.forEach(target => {
        target.removeEventListener('scroll', onScroll);
        target.removeEventListener('wheel', onScroll);
      });

      const targets = [window];
      const activeModal = getActiveModal();
      if (activeModal) {
        targets.push(activeModal);
        const modalContent = activeModal.querySelector('.modal-content');
        if (modalContent) {
          targets.push(modalContent);
          activeModal.querySelectorAll('*').forEach(element => {
            const style = window.getComputedStyle(element);
            if (style.overflowY === 'auto' || style.overflowY === 'scroll' || element.scrollHeight > element.clientHeight + 1) {
              targets.push(element);
            }
          });
        }
      }

      scrollTargets = [...new Set(targets)];
      scrollTargets.forEach(target => {
        target.addEventListener('scroll', onScroll, { passive: true });
        target.addEventListener('wheel', onScroll, { passive: true });
      });
    };
    refreshScrollTargets();

    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(syncObservedElements);
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      observer?.disconnect();
      mutationObserver?.disconnect();
      scrollTargets.forEach(target => {
        target.removeEventListener('scroll', onScroll);
        target.removeEventListener('wheel', onScroll);
      });
      if (fallbackScan) {
        window.removeEventListener('scroll', fallbackScan);
        window.removeEventListener('resize', fallbackScan);
      }
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, []);

  if (visibleKinds.size === 0) return null;

  return (
    <div
      className={`unit-toggle-root${isScrolling ? ' hidden' : ''}`}
      data-floating-control="units"
      aria-label="Units shown in the visible activity fields"
      aria-hidden={isScrolling}
    >
      <div className="unit-toggle-group" role="group" aria-label="Measurement system">
        <button
          className="unit-toggle-button active"
          onClick={toggleMeasurementSystem}
          title={`Switch to ${measurementSystem === 'imperial' ? 'metric' : 'imperial'} units`}
          aria-label={`${measurementSystem === 'imperial' ? 'Imperial' : 'Metric'} units. Switch to ${measurementSystem === 'imperial' ? 'metric' : 'imperial'} units`}
          tabIndex={isScrolling ? -1 : 0}
        >
          {measurementSystem === 'imperial' ? 'Imperial' : 'Metric'}
        </button>
      </div>
    </div>
  );
};

export default UnitToggle;
